import { useState, useEffect, useRef } from 'react';
import {
  getSubscriberProfile,
  getWalletSnapshot,
  getBankDetails,
  getFamilyMembers
} from '../services/subscriberWalletService';

export const useSubscriberData = (id, currentRole) => {
  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [bank, setBank] = useState(null);
  const [family, setFamily] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiTimeout, setApiTimeout] = useState(false);
  const isLoadingDataRef = useRef(false);

  useEffect(() => {
    if (!id || !currentRole || isLoadingDataRef.current) {
      return;
    }

    isLoadingDataRef.current = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setApiTimeout(false);
        
        const timeoutId = setTimeout(() => {
          setApiTimeout(true);
          setError('API request timeout. Please check your connection and try again.');
        }, 30000);
        
        const [profileData, walletData, bankData, familyData] = await Promise.allSettled([
          getSubscriberProfile(id, currentRole).catch(err => {
            return { error: 'Profile API failed', details: err.message };
          }),
          getWalletSnapshot(id, currentRole).catch(err => {
            return { error: 'Wallet API failed', details: err.message };
          }),
          getBankDetails(id, currentRole).catch(err => {
            return { error: 'Bank API failed', details: err.message };
          }),
          getFamilyMembers(id, currentRole).catch(err => {
            return { error: 'Family API failed', details: err.message };
          })
        ]);

        // Handle profile data
        if (profileData.status === 'fulfilled' && profileData.value) {
          const response = profileData.value;
          let profileToSet = null;
          
          if (response.msg && typeof response.msg === 'object') {
            profileToSet = response.msg;
          } else if (response.subscription || response.valid_period || response.plan) {
            profileToSet = response;
          } else if (response.data && typeof response.data === 'object') {
            profileToSet = response.data;
          } else if (response.success && response.data && typeof response.data === 'object') {
            profileToSet = response.data;
          } else {
            profileToSet = {
              id: response.id || id,
              personal: { 
                full_name: response.full_name || response.name || response.first_name || '', 
                mobile: response.mobile || response.phone || response.phone_number || '' 
              },
              subscription: response.subscription || response.subscription_id || '',
              valid_period: response.valid_period || response.period || response.validity_period || '',
              plan: response.plan || response.personal_subscription || response.plan_name || '',
              status: response.status || response.active || '',
              kyc: response.kyc || response.kyc_status || '',
              login_count: response.login_count || 0,
              first_login_date: response.first_login_date || null,
              last_login: response.last_login || null
            };
          }
          
          if (profileToSet) {
            setProfile(profileToSet);
          }
        }
        
        // Handle wallet data
        if (walletData.status === 'fulfilled' && walletData.value) {
          const response = walletData.value;
          if (response.msg && typeof response.msg === 'object') {
            setWallet(response.msg);
          } else if (response.data && typeof response.data === 'object') {
            setWallet(response.data);
          } else if (response.wallet_snapshot) {
            setWallet(response);
          } else {
            setWallet({ error: 'Invalid wallet data format' });
          }
        } else if (walletData.status === 'rejected' || (walletData.value && walletData.value.error)) {
          setWallet({ error: walletData.value?.error || 'Failed to load wallet data' });
        }
        
        // Handle bank data
        if (bankData.status === 'fulfilled' && bankData.value) {
          const response = bankData.value;
          if (response.msg && typeof response.msg === 'object') {
            setBank(response.msg);
          } else if (response.data && typeof response.data === 'object') {
            setBank(response.data);
          } else {
            setBank(null);
          }
        } else {
          setBank(null);
        }
        
        // Handle family data
        if (familyData.status === 'fulfilled' && familyData.value) {
          const response = familyData.value;
          // New structure: response.msg.items (with pagination)
          if (response.msg && response.msg.items && Array.isArray(response.msg.items)) {
            setFamily(response.msg.items);
          } 
          // Old structure: response.msg as array directly
          else if (response.msg && Array.isArray(response.msg)) {
            setFamily(response.msg);
          } 
          // Alternative: response.data as array
          else if (response.data && Array.isArray(response.data)) {
            setFamily(response.data);
          } 
          // Direct array response
          else if (Array.isArray(response)) {
            setFamily(response);
          } 
          // Fallback: empty array
          else {
            setFamily([]);
          }
        } else {
          setFamily([]);
        }
        
        clearTimeout(timeoutId);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load subscriber data');
      } finally {
        setLoading(false);
        isLoadingDataRef.current = false;
      }
    };
    
    loadData();
  }, [id, currentRole]);

  return {
    profile,
    wallet,
    bank,
    family,
    loading,
    error,
    apiTimeout,
    setFamily
  };
};

